import { isAxiosError } from 'axios'
import { apiClient } from '@/lib/api.ts'

export type DocType =
  | 'quotation' | 'order' | 'delivery' | 'invoice' | 'credit'
  | 'purchase_invoice' | 'purchase_credit' | 'purchase_down_payment'
export type DocStatus = 'open' | 'closed' | 'cancelled' | 'all'

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  quotation: 'Angebot',
  order: 'Auftrag',
  delivery: 'Lieferschein',
  invoice: 'Rechnung',
  credit: 'Gutschrift',
  purchase_invoice: 'Eingangsrechnung',
  purchase_credit: 'Eingangsgutschrift',
  purchase_down_payment: 'Anzahlungsrechnung',
}

export const ALLOWED_CONVERSIONS: Record<DocType, DocType[]> = {
  quotation: ['order'],
  order: ['delivery', 'invoice'],
  invoice: ['credit'],
  delivery: [],
  credit: [],
  purchase_invoice: [],
  purchase_credit: [],
  purchase_down_payment: [],
}

export interface CachedDocument {
  id: string
  tenantId: string
  docType: DocType
  sapDocEntry: number
  sapDocNum: number | null
  cardCode: string | null
  cardName: string | null
  docDate: string | null
  docDueDate: string | null
  docTotal: number | null
  docCurrency: string | null
  docStatus: string | null
  isClosed: boolean
  isCancelled: boolean
  comments: string | null
  linesJson: SapDocLine[] | null
  fullDataJson: Record<string, unknown> | null
  lastSyncedAt: string | null
  createdAt: string
  updatedAt: string
  /**
   * Herkunft des Dokuments — wird vom Backend gesetzt:
   * - 'sap' (oder undefined): reines SAP-Dokument
   * - 'ai-draft': lokale Quote (status draft/ready), noch nicht in SAP exportiert
   * - 'ai-exported': SAP-Dokument, das aus einem KI-Dialog stammt (mit lokaler Quote verknuepft)
   */
  source?: 'sap' | 'ai-draft' | 'ai-exported'
  /** Quote-ID fuer Klick-Navigation, falls source !== 'sap'. */
  localQuoteId?: string
}

/** Sentinel fuer Drafts ohne SAP-DocEntry (s. Backend `LOCAL_DRAFT_DOC_ENTRY_SENTINEL`). */
export const LOCAL_DRAFT_DOC_ENTRY = -1

export interface SapDocLine {
  LineNum?: number
  ItemCode?: string
  ItemDescription?: string
  Dscription?: string
  LineType?: string
  Quantity?: number
  UnitPrice?: number
  Price?: number
  DiscountPercent?: number
  LineTotal?: number
  Currency?: string
  FreeText?: string
  UoMEntry?: number
  MeasureUnit?: string
  UnitMsr?: string
  VatGroup?: string
  WarehouseCode?: string
  ProjectCode?: string
  /** BOM type: standard line / BOM parent / BOM sub-item */
  TreeType?: 'iNotATree' | 'iSalesTree' | 'iSubTree'
  /** Line number of the parent line (only when TreeType = 'iSubTree') */
  ParentLineNum?: number
}

export interface ListDocumentsParams {
  type?: DocType | DocType[]
  status?: DocStatus
  search?: string
  cardCode?: string
  page?: number
  pageSize?: number
  /** Mehrere Mandanten-IDs — liefert Dokumente aller genannten Mandanten integriert nach Datum sortiert. */
  tenantIds?: string[]
}

export interface ListDocumentsResult {
  items: CachedDocument[]
  total: number
  page: number
  pageSize: number
}

export interface CreateDocumentParams {
  docType: DocType
  cardCode: string
  positions: Array<{
    itemCode: string
    description: string
    quantity: number
    unitPrice: number
    discountPct?: number
  }>
  remarks?: string
  docDate?: string
  tenantId?: string
}

export interface ConvertDocumentParams {
  targetType: DocType
  modifiedLines?: SapDocLine[]
}

export interface ListDocumentsResponse extends ListDocumentsResult {
  cacheEmpty?: boolean
}

export async function listDocuments(params: ListDocumentsParams = {}): Promise<ListDocumentsResponse> {
  const query = new URLSearchParams()
  if (params.type) {
    const types = Array.isArray(params.type) ? params.type : [params.type]
    types.forEach((t) => query.append('type', t))
  }
  if (params.status) query.set('status', params.status)
  if (params.search) query.set('search', params.search)
  if (params.cardCode) query.set('cardCode', params.cardCode)
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.tenantIds && params.tenantIds.length > 0) {
    params.tenantIds.forEach((id) => query.append('tenantId', id))
  }
  const res = await apiClient.get<ListDocumentsResult>(`/sap-documents?${query.toString()}`)
  return {
    ...res.data,
    cacheEmpty: res.headers['x-cache-empty'] === 'true',
  }
}

export interface SapCacheOutOfSyncError {
  code: 'SAP_CACHE_OUT_OF_SYNC'
  tenantId: string
  companyDb: string
  docEntry: number
  rawError: string
}

export interface DocumentDetailResult extends CachedDocument {
  /** Gesetzt wenn SAP-Detail-Fetch fehlschlug aber gecachte Daten zurueckgegeben wurden. */
  _sapFetchError?: string
  /** Gesetzt wenn Cache-Eintrag existiert aber SAP das Dokument nicht mehr kennt (404). */
  _cacheOutOfSync?: SapCacheOutOfSyncError
}

export async function getDocumentDetail(
  docType: DocType,
  sapDocEntry: number,
  options?: { tenantId?: string },
): Promise<DocumentDetailResult> {
  const headers = options?.tenantId ? { 'x-tenant-id': options.tenantId } : undefined
  try {
    const res = await apiClient.get<CachedDocument & { _cacheOutOfSync?: SapCacheOutOfSyncError }>(
      `/sap-documents/${docType}/${sapDocEntry}`,
      { headers },
    )
    const sapFetchError = res.headers['x-sap-fetch-error'] as string | undefined
    const { _cacheOutOfSync, ...docData } = res.data
    return { ...docData, _sapFetchError: sapFetchError, _cacheOutOfSync }
  } catch (error) {
    if (isAxiosError(error)) {
      const responseData = error.response?.data as (CachedDocument & { _cacheOutOfSync?: SapCacheOutOfSyncError }) | undefined
      if (error.response?.status === 404 && responseData?._cacheOutOfSync) {
        const { _cacheOutOfSync, ...docData } = responseData
        return { ...docData, _cacheOutOfSync }
      }
    }
    throw error
  }
}

export async function createDocument(params: CreateDocumentParams): Promise<Record<string, unknown>> {
  const res = await apiClient.post<Record<string, unknown>>('/sap-documents', params)
  return res.data
}

export async function convertDocument(
  docType: DocType,
  sapDocEntry: number,
  params: ConvertDocumentParams,
): Promise<Record<string, unknown>> {
  const res = await apiClient.post<Record<string, unknown>>(
    `/sap-documents/${docType}/${sapDocEntry}/convert`,
    params,
  )
  return res.data
}

export async function triggerSync(options?: { tenantId?: string }): Promise<{ ok: boolean; message?: string }> {
  const params = options?.tenantId ? `?tenantId=${encodeURIComponent(options.tenantId)}` : ''
  const res = await apiClient.post<{ ok: boolean; message?: string }>(`/sap-documents/sync${params}`, {})
  return res.data
}

export function isDocumentOpen(doc: CachedDocument): boolean {
  if (doc.source === 'ai-draft') return true
  return doc.docStatus === 'bost_Open' && !doc.isClosed && !doc.isCancelled
}

export function formatDocNum(doc: CachedDocument): string {
  return doc.sapDocNum ? String(doc.sapDocNum) : `#${doc.sapDocEntry}`
}
